import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";

import EditTicketModal from "Events/Components/EditTicketModal";
import TicketGroupItem from "Events/Components/TicketGroupItem";
import Input from "shared/@forms/Input";
import Button from "shared/Button";
import { Select } from "shared/Select/Select";
import { Modal } from "shared/Modal";
import Checkbox from "shared/@forms/Checkbox";
import AddGroupModal from "../AddGroupModal";
import { GroupsGet } from "./GroupsGet";
import styles from "./TicketGroup.module.css";
import Loader from "shared/Loader";
import DatePicker from "../DatePicker";
import TimePicker from "../TimePicker";

import AddButton from "../AddButton";
import classNames from "classnames";
import moment from "moment-timezone";

import SelectPhotoModal from "../../V2Components/SelectPhotoModal";
import { Plus } from "react-ikonate";

import { useEventsVirtualGet } from "../NewEventModal/useEventsVirtualGet";

import {
  toIanaTimeZone,
  buildUtcFromLocalParts,
  utcToLocalParts,
  utcToLocalDateTimeDisplay,
} from "shared/dateTimeHelpers";

const getTicketTimezoneFieldForDate = (dateField, isLocalV2) => {
  if (dateField === "purchaseStartDate" || dateField === "purchaseEndDate") {
    return "purchaseStartTimeZone";
  }

  if (
    !isLocalV2 &&
    (dateField === "startFeeDate" || dateField === "changeableUntilDate")
  ) {
    return "purchaseStartTimeZone";
  }

  return dateField.replace("Date", "TimeZone");
};

const getTicketControlledDateFields = (timezoneField, isLocalV2) => {
  const fields = ["purchaseStartDate", "purchaseEndDate"];

  if (!isLocalV2) {
    fields.push("startFeeDate", "changeableUntilDate");
  }

  return fields.filter(
    (field) => getTicketTimezoneFieldForDate(field, isLocalV2) === timezoneField
  );
};

const getTicketFieldMeta = (ticket, field, isLocalV2, marketingInfo) => {
  const timezoneField = getTicketTimezoneFieldForDate(field, isLocalV2);
  const timeZoneLabel = getEffectiveTicketTimeZoneLabel(
    ticket,
    field,
    isLocalV2,
    marketingInfo
  );

  const rawUtc = ticket?.[field]?.saved ?? ticket?.[field]?.value ?? null;

  const { date, time } = utcToLocalParts(rawUtc, timeZoneLabel);

  return {
    timezoneField,
    timeZoneLabel,
    rawUtc,
    dateString: date || "",
    timeString: time || "",
    displayDateTime: rawUtc
      ? utcToLocalDateTimeDisplay(rawUtc, timeZoneLabel)
      : "N/A",
  };
};

const getEventPurchaseTimeZoneLabel = (marketingInfo) =>
  marketingInfo?.event?.dates?.purchaseStartTimeZone?.saved ??
  marketingInfo?.event?.dates?.purchaseStartTimeZone?.value;

const getEffectiveTicketTimeZoneLabel = (
  ticket,
  field,
  isLocalV2,
  marketingInfo
) => {
  const timezoneField = getTicketTimezoneFieldForDate(field, isLocalV2);

  return (
    ticket?.[timezoneField]?.saved ??
    ticket?.[timezoneField]?.value ??
    getEventPurchaseTimeZoneLabel(marketingInfo)
  );
};

const getExplicitTicketTimeZoneLabel = (ticket, field, isLocalV2) => {
  const timezoneField = getTicketTimezoneFieldForDate(field, isLocalV2);

  return ticket?.[timezoneField]?.saved ?? ticket?.[timezoneField]?.value ?? "";
};

export function TicketGroup({
  name,
  available,
  setAvailable,
  limit,
  setLimit,
  ticketsPer,
  setTicketsPer,
  tickets,
  updateTickets,
  eventStartDate,
  isMajorV1 = false,
  isV2,
  isLocalV2,
  ticketSet = [],
  setTicketSet,
  removeTicketSet,
  onAddTicket,
  homesitesGroups,
  isPublished,
  isVirtual,
  eventName,
  marketingInfo,
  pageMode,
  isLocal2,
}) {
  const [virtualEvents, setVirtualEvents] = useState([]);
  const [isVirtualModalOpen, setIsVirtualModalOpen] = useState(false);
  const [selectedVirtualTicketId, setSelectedVirtualTicketId] = useState([]);

  const isVirtualEventsPage =
    (marketingInfo?.event?.location?.saved ||
      marketingInfo?.event?.location?.value) == "virtual";

  const {
    data: virtualEventsData,
    isLoading: isVirtualEventsLoading,
    refetch: refetchVirtualEvents,
  } = useEventsVirtualGet({
    ruleset:
      marketingInfo?.event?.ruleset?.saved ||
      marketingInfo?.event?.ruleset?.value,
    eventName:
      marketingInfo?.event?.name?.saved || marketingInfo?.event?.name?.value,
    enabled: !isVirtualEventsPage,
  });

  useEffect(() => {
    if (virtualEventsData) {
      setVirtualEvents(virtualEventsData);
    }
  }, [virtualEventsData]);

  const handleVirtualTicketChange = () => {
    const ticketToUpdate = ticketSet.tickets.find(
      (ticket) => ticket.id === selectedTicketId
    );

    if (ticketToUpdate) {
      const updatedTicket = {
        ...ticketToUpdate,
        linkedVirtualTicket: {
          ...ticketToUpdate.linkedVirtualTicket,
          saved: {
            ...ticketToUpdate.linkedVirtualTicket.saved,
            ticketId: selectedVirtualTicketId,
            name: selectedVirtualTicketName,
          },
        },
      };

      const updatedTickets = ticketSet.tickets.map((ticket) =>
        ticket.id === selectedTicketId ? updatedTicket : ticket
      );

      setTicketSet({
        ...ticketSet,
        tickets: updatedTickets,
      });
    }
  };

  const handleRemoveLinkedTicket = (ticketId) => {
    const ticketToUpdate = ticketSet.tickets.find(
      (ticket) => ticket.id === ticketId
    );

    if (ticketToUpdate) {
      const updatedTicket = {
        ...ticketToUpdate,
        linkedVirtualTicket: {
          ...ticketToUpdate.linkedVirtualTicket,
          saved: {
            ...ticketToUpdate.linkedVirtualTicket.saved,
            ticketId: null,
            name: null,
          },
        },
      };

      const updatedTickets = ticketSet.tickets.map((ticket) =>
        ticket.id === ticketId ? updatedTicket : ticket
      );

      setTicketSet({
        ...ticketSet,
        tickets: updatedTickets,
      });
    }
  };

  const { data: groupData, isLoading: isGroupsLoading, refetch } = GroupsGet();

  const timeZones = [
    "Pacific Time (PT)",
    "Mountain Time (MT)",
    "Central Time (CT)",
    "Eastern Time (ET)",
    "Alaska Time (AKT)",
    "Hawaii-Aleutian Time (HAT)",
    "Hawaii-Honolulu (HST)",
  ];

  const [showEditPurchaseLimits, setShowEditPurchaseLimits] = useState(false);
  const [ticketsPerField, setTicketsPerField] = useState("Tickets Per User");
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTicket, setEditingTicket] = useState({});
  const [groups, setGroups] = useState([]);
  const [selectedTicketId, setSelectedTicketId] = useState("");
  const [ticketsWithGroups, setTicketsWithGroups] = useState(
    ticketSet.tickets
      ? ticketSet.tickets.map((ticket) => ({
          ticketId: ticket.id,
          groups:
            ticket.limitedToGroups.saved ?? ticket.limitedToGroups.value ?? [],
        }))
      : []
  );
  const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
  const acceptedCreditOptions = [
    {
      creditTypeId: "LF",
      name: "Local Function Credit",
    },
    {
      creditTypeId: "MF",
      name: "Major Function Credit",
    },
    {
      creditTypeId: "FE25_FPL",
      name: "FE 2025 - Founders Platinum Credit",
    },
    {
      creditTypeId: "LS26_FPL",
      name: "LS 2026 - Founders Platinum Credit",
    },
    {
      creditTypeId: "FR26_FPL",
      name: "FR 2026 - Founders Platinum Credit",
    },
  ];
  const [isAcceptedCreditModalOpen, setIsAcceptedCreditModalOpen] =
    useState(false);
  const [acceptedCreditArray, setAcceptedCreditArray] = useState([]);

  const [isPurchaseDatesModalOpen, setIsPurchaseDatesModalOpen] =
    useState(false);

  const [selectedVirtualTicketName, setSelectedVirtualTicketName] = useState(
    []
  );
  const [selectedTimeZone, setSelectedTimeZone] = useState(null);

  // useEffect(() => {
  //   if (isPurchaseDatesModalOpen && selectedTicketId) {
  //     const selectedTicket = ticketSet.tickets.find(
  //       (t) => t.id === selectedTicketId
  //     );
  //     setSelectedTimeZone(
  //       selectedTicket?.purchaseStartTimeZone?.saved ??
  //         selectedTicket?.purchaseStartTimeZone?.value
  //       //?? getEventPurchaseTimeZoneLabel(marketingInfo)
  //     );
  //   }
  // }, [isPurchaseDatesModalOpen, selectedTicketId]);

  useEffect(() => {
    if (groupData) {
      setGroups(groupData);
    }
  }, [groupData]);

  useEffect(() => {
    if (ticketSet.tickets && ticketsWithGroups.length === 0) {
      setTicketsWithGroups(
        ticketSet.tickets.map((ticket) => {
          return {
            ticketId: ticket.id,
            groups: [],
          };
        })
      );
    }
  }, [ticketSet.tickets]);

  const [isRequired, setIsRequired] = useState({
    title: false,
    subTitle: false,
    icon: false,
    name: false,
    price: false,
    commonItemId: false,
  });

  useEffect(() => {
    const updatedIsRequired = { ...isRequired };

    if (ticketSet?.title?.required !== undefined) {
      updatedIsRequired.title = ticketSet.title.required;
    }
    if (ticketSet?.subTitle?.required !== undefined) {
      updatedIsRequired.subTitle = ticketSet.subTitle.required;
    }
    if (ticketSet?.icon?.required !== undefined) {
      updatedIsRequired.icon = ticketSet.icon.required;
    }
    if (ticketSet?.tickets?.[0]?.name?.required !== undefined) {
      updatedIsRequired.name = ticketSet.tickets[0].name.required;
    }
    if (ticketSet?.tickets?.[0]?.price?.required !== undefined) {
      updatedIsRequired.price = ticketSet.tickets[0].price.required;
    }
    if (ticketSet?.tickets?.[0]?.commonItemId?.required !== undefined) {
      updatedIsRequired.commonItemId =
        ticketSet.tickets[0].commonItemId.required;
    }

    setIsRequired(updatedIsRequired);
  }, [ticketSet]);

  const handleTicketSetInput = (field, value) => {
    setTicketSet({
      ...ticketSet,
      _id: ticketSet.id,
      [field]: {
        ...ticketSet[field],
        saved: value,
      },
    });
  };

  const [showIconModal, setShowIconModal] = useState(false);

  const handleSubmitIcon = (selectedIcon) => {
    if (selectedIcon) {
      setTicketSet({
        ...ticketSet,
        icon: {
          ...ticketSet.icon,
          saved: selectedIcon,
          value: selectedIcon,
        },
      });
    }
  };

  const handleAddGroups = (newGroups) => {
    if (selectedTicketId === "ticketSet") {
      const currentBadges = ticketSet.badges.saved !== null && ticketSet.badges.saved !== undefined
        ? ticketSet.badges.saved
        : ticketSet.badges.value || [];

      const updatedBadges = [
        ...currentBadges,
        ...newGroups.filter(
          (newGroup) =>
            !currentBadges.some(
              (existingBadge) => existingBadge.name === newGroup.name
            )
        ),
      ];

      setTicketSet({
        ...ticketSet,
        badges: { ...ticketSet.badges, saved: updatedBadges },
      });
    } else {
      const ticketToUpdate = ticketSet.tickets.find(
        (ticket) => ticket.id === selectedTicketId
      );

      if (ticketToUpdate) {
        const currentGroups = ticketToUpdate.limitedToGroups.saved !== null && ticketToUpdate.limitedToGroups.saved !== undefined
          ? ticketToUpdate.limitedToGroups.saved
          : ticketToUpdate.limitedToGroups.value || [];

        const updatedGroups = [
          ...currentGroups,
          ...newGroups.filter(
            (newGroup) =>
              !currentGroups.some(
                (existingGroup) => existingGroup.id === newGroup.id
              )
          ),
        ];

        const updatedTicket = {
          ...ticketToUpdate,
          limitedToGroups: {
            ...ticketToUpdate.limitedToGroups,
            saved: updatedGroups,
          },
        };

        const updatedTickets = ticketSet.tickets.map((ticket) =>
          ticket.id === selectedTicketId ? updatedTicket : ticket
        );

        setTicketSet({
          ...ticketSet,
          tickets: updatedTickets,
        });
      }
    }
  };

  const handleRemoveGroup = (groupToRemove, targetId) => {
    if (targetId === "ticketSet") {
      const currentBadges = ticketSet.badges.saved !== null && ticketSet.badges.saved !== undefined
        ? ticketSet.badges.saved
        : ticketSet.badges.value || [];

      const updatedBadges = currentBadges.filter(
        (badge) => badge.id !== groupToRemove.id
      );

      setTicketSet({
        ...ticketSet,
        badges: { ...ticketSet.badges, saved: updatedBadges },
      });
    } else {
      const ticketToUpdate = ticketSet.tickets.find(
        (ticket) => ticket.id === targetId
      );

      if (ticketToUpdate) {
        const currentGroups = ticketToUpdate.limitedToGroups.saved !== null && ticketToUpdate.limitedToGroups.saved !== undefined
          ? ticketToUpdate.limitedToGroups.saved
          : ticketToUpdate.limitedToGroups.value || [];

        const updatedGroups = currentGroups.filter(
          (group) => group.id !== groupToRemove.id
        );

        const updatedTicket = {
          ...ticketToUpdate,
          limitedToGroups: {
            ...ticketToUpdate.limitedToGroups,
            saved: updatedGroups,
          },
        };

        const updatedTickets = ticketSet.tickets.map((ticket) =>
          ticket.id === targetId ? updatedTicket : ticket
        );

        setTicketSet({
          ...ticketSet,
          tickets: updatedTickets,
        });
      }
    }
  };
  const handleRemoveCreditType = (typeToRemove, targetId) => {
    const ticketToUpdate = ticketSet.tickets.find(
      (ticket) => ticket.id === targetId
    );

    if (ticketToUpdate) {
      const currentAcceptedCreditTypes = ticketToUpdate.acceptedCreditTypes
        .saved
        ? ticketToUpdate.acceptedCreditTypes.saved
        : ticketToUpdate.acceptedCreditTypes.value || [];

      const updatedCreditTypes = currentAcceptedCreditTypes.filter(
        (type) => type.creditTypeId !== typeToRemove.creditTypeId
      );

      const updatedTicket = {
        ...ticketToUpdate,
        acceptedCreditTypes: {
          ...ticketToUpdate.acceptedCreditTypes,
          saved: updatedCreditTypes,
        },
      };

      const updatedTickets = ticketSet.tickets.map((ticket) =>
        ticket.id === targetId ? updatedTicket : ticket
      );

      setTicketSet({
        ...ticketSet,
        tickets: updatedTickets,
      });
    }
  };
  const handleCheckBoxClick = (ticketId) => {
    const ticketToUpdate = ticketSet.tickets.find(
      (ticket) => ticket.id === ticketId
    );

    if (ticketToUpdate) {
      const updatedTicket = {
        ...ticketToUpdate,
        creditsRequired: {
          ...ticketToUpdate.creditsRequired,
          saved: !(
            ticketToUpdate.creditsRequired.saved ??
            ticketToUpdate.creditsRequired.value
          ),
        },
      };

      const updatedTickets = ticketSet.tickets.map((ticket) =>
        ticket.id === ticketId ? updatedTicket : ticket
      );

      setTicketSet({
        ...ticketSet,
        tickets: updatedTickets,
      });
    }
  };

  const handleSelectChange = (newValue, ticketId, field) => {
    const ticketToUpdate = ticketSet.tickets.find(
      (ticket) => ticket.id === ticketId
    );

    if (ticketToUpdate) {
      const updatedTicket = {
        ...ticketToUpdate,
        [field]: { ...ticketToUpdate[field], saved: newValue },
      };

      const updatedTickets = ticketSet.tickets.map((ticket) =>
        ticket.id === ticketId ? updatedTicket : ticket
      );

      setTicketSet({
        ...ticketSet,
        tickets: updatedTickets,
      });
    }
  };
  const handleCreditChange = () => {
    const ticketToUpdate = ticketSet.tickets.find(
      (ticket) => ticket.id === selectedTicketId
    );

    if (ticketToUpdate) {
      const updatedTicket = {
        ...ticketToUpdate,
        acceptedCreditTypes: {
          ...ticketToUpdate.acceptedCreditTypes,
          saved: acceptedCreditArray,
        },
      };

      const updatedTickets = ticketSet.tickets.map((ticket) =>
        ticket.id === selectedTicketId ? updatedTicket : ticket
      );

      setTicketSet({
        ...ticketSet,
        tickets: updatedTickets,
      });
    }
  };
  const handleInputChange = (value, field, ticketId) => {
    const ticketToUpdate = ticketSet.tickets.find(
      (ticket) => ticket.id === ticketId
    );

    if (ticketToUpdate) {
      const updatedTicket = {
        ...ticketToUpdate,
        [field]: { ...ticketToUpdate[field], saved: value },
      };

      const updatedTickets = ticketSet.tickets.map((ticket) =>
        ticket.id === ticketId ? updatedTicket : ticket
      );

      setTicketSet({
        ...ticketSet,
        tickets: updatedTickets,
      });
    }
  };

  const updateUtcDateField = (newValue, field, isTime = false) => {
    const updatedTicketSet = {
      ...ticketSet,
      tickets: ticketSet.tickets.map((tkt) => {
        if (tkt.id !== selectedTicketId) return tkt;

        const timeZoneLabel = getEffectiveTicketTimeZoneLabel(
          tkt,
          field,
          isLocalV2,
          marketingInfo
        );

        const rawUtc = tkt?.[field]?.saved ?? tkt?.[field]?.value ?? null;
        const { date: currentDate, time: currentTime } = utcToLocalParts(
          rawUtc,
          timeZoneLabel
        );

        let localDate = currentDate;
        let localTime = currentTime || "00:00";

        if (isTime) {
          if (!localDate || !moment(localDate, "MM/DD/YYYY", true).isValid()) {
            localDate = moment().format("MM/DD/YYYY");
          }
          localTime = newValue;
        } else {
          if (!moment(newValue, "MM/DD/YYYY", true).isValid()) {
            return tkt;
          }
          localDate = newValue;
        }

        const rebuiltUtc = buildUtcFromLocalParts({
          localDate,
          localTime,
          timeZone: timeZoneLabel,
        });

        return {
          ...tkt,
          [field]: {
            ...tkt[field],
            saved: rebuiltUtc,
          },
        };
      }),
    };

    setTicketSet(updatedTicketSet);
  };

  const handleTimeZoneChange = (
    newTimeZoneLabel,
    timezoneField = "purchaseStartTimeZone"
  ) => {
    //setSelectedTimeZone(newTimeZoneLabel);

    const updatedTicketSet = {
      ...ticketSet,
      tickets: ticketSet.tickets.map((tkt) => {
        if (tkt.id !== selectedTicketId) return tkt;

        const oldTimeZoneLabel =
          tkt?.[timezoneField]?.saved ??
          tkt?.[timezoneField]?.value ??
          getEventPurchaseTimeZoneLabel(marketingInfo);

        const updatedTicket = {
          ...tkt,
          [timezoneField]: {
            ...tkt[timezoneField],
            saved: newTimeZoneLabel,
          },
        };

        const controlledFields = getTicketControlledDateFields(
          timezoneField,
          isLocalV2
        );

        controlledFields.forEach((field) => {
          const rawUtc = tkt?.[field]?.saved ?? tkt?.[field]?.value ?? null;

          if (!rawUtc) {
            return;
          }

          const { date: localDate, time: localTime } = utcToLocalParts(
            rawUtc,
            oldTimeZoneLabel
          );

          if (!localDate) {
            return;
          }

          const rebuiltUtc = buildUtcFromLocalParts({
            localDate,
            localTime: localTime || "00:00",
            timeZone: newTimeZoneLabel,
          });

          if (rebuiltUtc) {
            updatedTicket[field] = {
              ...tkt[field],
              saved: rebuiltUtc,
            };
          }
        });

        return updatedTicket;
      }),
    };

    setTicketSet(updatedTicketSet);
  };

  const handleRemoveTicket = (ticketId) => {
    const newTicketSet = {
      ...ticketSet,
      tickets: ticketSet.tickets.filter((tkt) => tkt.id !== ticketId),
    };
    if (newTicketSet.tickets.length < 1) {
      removeTicketSet();
    } else {
      setTicketSet({
        ...ticketSet,
        tickets: ticketSet.tickets.filter((tkt) => tkt.id !== ticketId),
      });
    }
  };

  const handleEditOpen = (ticket) => {
    setEditingTicket({
      id: ticket.id,
      name: ticket.name,
      price: ticket.price.toString(),
      startDate: ticket.startDate,
      endDate: ticket.endDate,
      startTime: ticket.startTime,
      endTime: ticket.endTime,
      timeZone: ticket.timeZone,
    });

    setTimeout(() => {
      setShowEditModal(true);
    }, 0);
  };

  const handleEditClose = () => {
    setShowEditModal(false);
    setEditingTicket({});
  };

  const handleSaveEdit = (newTicket) => {
    const updatedTickets = tickets.map((ticket) => {
      if (ticket.id === newTicket.id) {
        return {
          ...ticket,
          name: newTicket.name,
          price: parseInt(newTicket.price),
          startDate: newTicket.startDate,
          startTime: newTicket.startTime,
          endDate: newTicket.endDate,
          endTime: newTicket.endTime,
          timeZone: newTicket.timeZone,
        };
      }
      return ticket;
    });

    updateTickets(updatedTickets);
    handleEditClose();
  };

  useEffect(() => {
    switch (limit) {
      case "Limit tickets per customer (IBO)":
        setTicketsPerField("Max Tickets Per Customer");
        break;
      default:
        setTicketsPerField("Tickets Per User");
        break;
    }
  }, [limit]);

  const isReadOnlyPage = pageMode === "view";

  const canEditField = (permission, allowRepublish = false) =>
    permission === "write" &&
    !isReadOnlyPage &&
    (!isPublished || allowRepublish);

  function getEditViewOrHide(field) {}

  return (
    <>
      {isMajorV1 ? (
        // Layout for when isMajorV1 is true
        <div className={styles.ticketGroupContainer}>
          <div className={styles.header}>
            <span className={styles.label}>Ticket Set</span>
          </div>
          <div className={styles.body}>
            <div className={styles.ticketTableHeaderV1}>
              <span className={styles.ticketItem}>Ticket ID</span>
              <span>Ticket Name</span>
              <span className={styles.ticketPrice}>Price</span>
            </div>

            <div className={styles.ticketTableBody}>
              {tickets &&
                tickets?.map((ticket, index) => (
                  <div className={styles.ticketTableRow} key={index}>
                    <TicketGroupItem
                      ticket={ticket}
                      onEdit={handleEditOpen}
                      isMajorV1={isMajorV1}
                    />
                  </div>
                ))}
              <div className={styles.ticketTableRow}></div>
            </div>
          </div>
        </div>
      ) : isV2 ? (
        <div className={styles.ticketGroupContainer}>
          <div className={styles.v2header}>
            <span className={styles.label}>
              {ticketSet.ticketSet?.saved ?? ticketSet.ticketSet?.value} Ticket
              Set
            </span>
            {(ticketSet.ticketSetType?.saved ??
              ticketSet.ticketSetType?.value) === "sub" &&
              pageMode !== "view" &&
              !isPublished && (
                <div className={styles.subRemoveBtnContainer}>
                  <Button
                    className={styles.subRemoveBtn}
                    children={"Remove"}
                    onClick={() => removeTicketSet()}
                  />
                </div>
              )}
          </div>

          <div className={styles.inputContainer}>
            <div className={styles.inputLabelContainer}>
              <div className={styles.inputLabel}>
                {isLocal2 ? "Ticket Name " : "Display Name "}
                {isRequired.title && pageMode !== "view" ? " *" : ""}
              </div>
            </div>
            <div className={styles.titleInput}>
              {canEditField(ticketSet?.title?.permission, true) ? (
                <Input
                  label={isLocal2 ? "Ticket Name" : "Display Name"}
                  value={
                    (ticketSet?.title?.saved ?? ticketSet?.title?.value) ?? ""
                  }
                  placeholder={"Click to type"}
                  onChange={(e) =>
                    handleTicketSetInput("title", e.target.value)
                  }
                />
              ) : ticketSet?.title?.permission !== "hidden" ? (
                (ticketSet?.title?.saved ?? ticketSet?.title?.value) ?? ""
              ) : (
                <></>
              )}
            </div>
          </div>
          <div className={styles.inputContainer}>
            <div className={styles.inputLabelContainer}>
              <div className={styles.inputLabel}>
                {isLocal2 ? "Date(s) " : " Display Date(s) "}

                {isRequired.subTitle && pageMode !== "view" ? " *" : ""}
              </div>
            </div>
            <div className={styles.titleInput}>
              {canEditField(ticketSet?.subTitle?.permission, true) ? (
                <Input
                  label={isLocal2 ? "Date(s) " : " Display Date(s) "}
                  value={
                    (ticketSet?.subTitle?.saved ?? ticketSet?.subTitle?.value) ?? ""
                  }
                  placeholder={"Click to type"}
                  onChange={(e) =>
                    handleTicketSetInput("subTitle", e.target.value)
                  }
                />
              ) : ticketSet?.subTitle?.permission !== "hidden" ? (
                (ticketSet?.subTitle?.saved ?? ticketSet?.subTitle?.value) ?? "N/A"
              ) : (
                <></>
              )}
            </div>
          </div>

          <div className={styles.inputContainer}>
            <div className={styles.inputLabelContainer}>
              <div className={styles.inputLabel}>
                Icon {isRequired.icon && pageMode !== "view" ? " *" : ""}
              </div>
            </div>

            <div
              style={{
                pointerEvents:
                  canEditField(ticketSet?.icon?.permission, true)
                    ? "auto"
                    : "none",
              }}
            >
            {canEditField(ticketSet?.icon?.permission, true) && (
                  <div className={styles.iconContainer}>
                    {(ticketSet.icon?.saved ?? ticketSet.icon?.value) ? (
                      <img
                        src={ticketSet.icon.saved ?? ticketSet.icon.value}
                        alt="Selected"
                        className={styles.icon}
                        onClick={() => setShowIconModal(true)}
                      />
                    ) : (
                      <button
                        type="button"
                        onClick={() => setShowIconModal(true)}
                        className={classNames(
                          styles.imagePickerUploadButtonSmall
                        )}
                      >
                        <Plus
                          className={styles.imagePickerPlusSmall}
                          borderwidth={5}
                        />
                      </button>
                    )}
                  </div>
                )}
              {showIconModal && (
                <SelectPhotoModal
                  isVisible={showIconModal}
                  setIsVisible={setShowIconModal}
                  onSubmit={handleSubmitIcon}
                  folder="icons"
                />
              )}
            </div>
          </div>
          <div>
            {canEditField(ticketSet?.badges?.permission, true) ? (
              <div className={styles.bottomInputContainer}>
                <div className={styles.inputLabelContainer}>
                  <div className={styles.inputLabel}>Badges</div>
                </div>
                <div
                  className={styles.addBadgesButton}
                  onClick={() => {
                    setSelectedTicketId("ticketSet");
                    setIsGroupModalOpen(true);
                  }}
                >
                  <AddButton description={"Add Badges"} color="#2A3847" />
                </div>
                <div className={styles.badgesList}>
                  {(ticketSet.badges?.saved ?? ticketSet.badges?.value).map(
                    (group) => (
                      <div className={styles.speakerItem}>
                        {group?.name || "No Name For Group"}
                        {canEditField(ticketSet?.badges?.permission, true) && (
                          <div
                            className={styles.removeSpeaker}
                            onClick={() => handleRemoveGroup(group, "ticketSet")}
                          >
                            &times;
                          </div>
                        )}
                      </div>
                    )
                  )}
                </div>
              </div>
            ) : ticketSet?.badges?.permission !== "hidden" ? (
              <div className={styles.bottomInputContainer}>
                <div className={styles.inputLabelContainer}>
                  <div className={styles.inputLabel}>Badges</div>
                </div>
                <div className={styles.badgesListView}>
                  {(ticketSet.badges?.saved ?? ticketSet.badges?.value)
                    ?.length > 0 ? (
                    (ticketSet.badges?.saved ?? ticketSet.badges?.value).map(
                      (group) => (
                        <div className={styles.speakerItem}>
                          {group?.name || "No Name For Group"}
                          <div className={styles.removeSpeaker}></div>
                        </div>
                      )
                    )
                  ) : (
                    <div className={styles.speakerItem1}>N/A</div>
                  )}
                </div>
              </div>
            ) : (
              <></>
            )}
          </div>
          <div className={styles.body}>
            <div
              className={
                isVirtual ||
                (ticketSet.ticketSetType?.saved ??
                  ticketSet.ticketSetType?.value) === "sub" ||
                !(
                  marketingInfo.virtual?.linkedEvent?.saved?.id ||
                  marketingInfo.virtual?.linkedEvent?.value?.id
                )
                  ? styles.ticketTableHeaderV2
                  : styles.ticketTableHeaderV2Virtual
              }
            >
              <span>
                Name {isRequired.name && pageMode !== "view" ? " *" : ""}
              </span>
              <span className={styles.ticketPrice}>
                Price {isRequired.price && pageMode !== "view" ? " *" : ""}
              </span>
              <span>
                ID {isRequired.commonItemId && pageMode !== "view" ? " *" : ""}
              </span>
              <span className={styles.ticketPrice}>Cancel Fee</span>
              <span>
                Cancel Fee ID{" "}
                {isRequired.cancelFeeItemId && pageMode !== "view" ? " *" : ""}
              </span>
              {!isVirtual &&
                !(
                  (ticketSet.ticketSetType?.saved ??
                    ticketSet.ticketSetType?.value) === "sub"
                ) &&
                (marketingInfo.virtual?.linkedEvent?.saved?.id ||
                  marketingInfo.virtual?.linkedEvent?.value?.id) && (
                  <span>Linked Virtual Ticket</span>
                )}
              <span className={styles.creditsRequiredHeader}>Credits</span>
              <span>Groups</span>
              <span className={styles.creditsRequiredHeader}>Dates</span>
              <span />
            </div>
            <div className={styles.ticketTableBody}>
              {ticketSet.tickets && ticketsWithGroups.length > 0 ? (
                ticketSet?.tickets.map((ticket) => {
                  const ticketGroupData = ticketsWithGroups.find(
                    (twg) => twg.ticketId === ticket.id
                  );

                  if (!ticket.id) {
                    return <Loader />;
                  }
                  return (
                    <TicketGroupItem
                      key={`${ticketSet.id}-${ticket.id}`}
                      ticket={ticket}
                      onEdit={handleEditOpen}
                      isMajorV1={false}
                      isV2={true}
                      onAddGroup={(ticketId) => {
                        setSelectedTicketId(ticketId);
                        setIsGroupModalOpen(true);
                      }}
                      selectedGroups={
                        ticketGroupData ? ticketGroupData.groups : []
                      }
                      onRemoveGroup={(group, ticketId) =>
                        handleRemoveGroup(group, ticketId)
                      }
                      onAddLinkedTicket={(
                        ticketId,
                        linkedTicketId,
                        linkedTicketName
                      ) => {
                        setSelectedTicketId(ticketId);
                        setSelectedVirtualTicketId(linkedTicketId);
                        setSelectedVirtualTicketName(linkedTicketName);
                        setIsVirtualModalOpen(true);
                      }}
                      onRemoveLinkedTicket={(ticketId) =>
                        handleRemoveLinkedTicket(ticketId)
                      }
                      onRemoveCreditType={(type, ticketId) =>
                        handleRemoveCreditType(type, ticketId)
                      }
                      onCheckbox={(ticketId) => handleCheckBoxClick(ticketId)}
                      onSelect={(newValue, ticketId, field) =>
                        handleSelectChange(newValue, ticketId, field)
                      }
                      onCreditChange={(ticketId) => {
                        setSelectedTicketId(ticketId);
                        setAcceptedCreditArray(
                          ticketSet.tickets.find((tkt) => tkt.id === ticketId)
                            ?.acceptedCreditTypes?.saved
                            ? ticketSet.tickets.find(
                                (tkt) => tkt.id === ticketId
                              )?.acceptedCreditTypes?.saved
                            : ticketSet.tickets.find(
                                (tkt) => tkt.id === ticketId
                              )?.acceptedCreditTypes?.value
                        );
                        setIsAcceptedCreditModalOpen(true);
                      }}
                      onEditDates={(ticketId) => {
                        setSelectedTicketId(ticketId);
                        setIsPurchaseDatesModalOpen(true);
                      }}
                      isSub={
                        (ticketSet.ticketSetType?.saved ??
                          ticketSet.ticketSetType?.value) === "sub"
                      }
                      onInputChange={(value, field) =>
                        handleInputChange(value, field, ticket.id)
                      }
                      onRemoveTicket={(ticketId) =>
                        handleRemoveTicket(ticketId)
                      }
                      isPublished={isPublished}
                      pageMode={pageMode}
                      isVirtual={isVirtual}
                      eventName={eventName}
                      showLinkedVirtualTicketColumn={
                        marketingInfo.virtual?.linkedEvent?.saved?.id ||
                        marketingInfo.virtual?.linkedEvent?.value?.id
                      }
                    />
                  );
                })
              ) : (
                <div>
                  <Loader />
                </div>
              )}
              {/* {(ticketSet.ticketSetType?.saved ??
                ticketSet.ticketSetType?.value) === "sub" ||
                (isSummerConference && ( */}
              {!isPublished && (
                <div className={styles.addBtn} onClick={() => onAddTicket()}>
                  <AddButton description={"Add New Ticket"} color="#9AB5BB" />
                </div>
              )}

              {/* ))} */}
              <div className={styles.ticketTableRow}></div>
            </div>
          </div>
        </div>
      ) : (
        // Layout for when isMajorV1 is false
        <div className={styles.ticketGroupContainer}>
          <div className={styles.header}>
            <span className={styles.label}>Ticket Set</span>
            <span className={styles.name}>{name}</span>
            <span className={styles.ticketsAvailable}>Tickets Available</span>
            <span className={styles.ticketsAvailInput}>
              <span className={styles.numberSign}>#</span>
              <Input
                value={available || ""}
                onChange={(e) => setAvailable(parseInt(e.target.value))}
                onFocus={(e) => e.target.select()}
              />
            </span>
          </div>
          <div className={styles.body}>
            {showEditPurchaseLimits ? (
              <div className={styles.purchaseEditContainer}>
                <span className={styles.label}>Purchase Limits</span>
                <span className={styles.purchaseLimitsSelect}>
                  <Select
                    options={[
                      "Limit tickets per each user",
                      "Limit tickets per customer (IBO)",
                    ]}
                    placeholder={limit}
                    onChange={(value) => setLimit(value)}
                  />
                </span>
                <span className={styles.ticketsPerUser}>{ticketsPerField}</span>
                <span className={styles.ticketsPerInput}>
                  <Input
                    value={ticketsPer || ""}
                    onChange={(e) => setTicketsPer(e.target.value)}
                    placeholder="#"
                  />
                </span>

                <Button
                  size="small"
                  onClick={() => setShowEditPurchaseLimits(false)}
                  className={styles.editButton}
                >
                  Save
                </Button>
              </div>
            ) : (
              <div className={styles.purchaseEditContainer}>
                <span className={styles.label}>Purchase Limits</span>
                <span>{limit}</span>
                <span className={styles.ticketsPerUser}>{ticketsPerField}</span>
                <span className={styles.ticketsPerInput}>{ticketsPer}</span>

                <Button
                  size="small"
                  onClick={() => setShowEditPurchaseLimits(true)}
                  className={styles.editButton}
                >
                  Edit
                </Button>
              </div>
            )}
            <div className={styles.ticketTableHeader}>
              <span className={styles.ticketItem}>Ticket ID</span>
              <span>Ticket Name</span>
              <span className={styles.ticketPrice}>Price</span>
              <span>Purchase Begin</span>
              <span>Purchase End</span>
              <span>Time Zone</span>
              <span></span>
            </div>
            {editingTicket && (
              <div>
                <EditTicketModal
                  isOpen={showEditModal}
                  onCollapse={handleEditClose}
                  ticketInfo={editingTicket}
                  onClick={handleSaveEdit}
                />
              </div>
            )}

            <div className={styles.ticketTableBody}>
              {tickets?.map((ticket, index) => (
                <div className={styles.ticketTableRow} key={index}>
                  <TicketGroupItem
                    ticket={ticket}
                    onEdit={handleEditOpen}
                    isMajorV1={isMajorV1}
                    isV2={isV2}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <AddGroupModal
        isOpen={isGroupModalOpen}
        onCollapse={() => setIsGroupModalOpen(false)}
        onClick={(newGroups) => handleAddGroups(newGroups)}
        // groups={[...groups, ...homesitesGroups]}
        // groups={[...groups]}
        groups={homesitesGroups ? [...groups, ...homesitesGroups] : [...groups]}
        groupsLoading={isGroupsLoading}
      />

      <Modal
        title="Credits"
        isOpen={isAcceptedCreditModalOpen}
        onClose={() => setIsAcceptedCreditModalOpen(false)}
        children={
          <div className={styles.modal}>
            <div className={styles.optionsListContainer}>
              <span className={styles.checkbox}>
                {(() => {
                  const ticket =
                    ticketSet.tickets.find(
                      (tkt) => tkt.id == selectedTicketId
                    ) || null;

                  if (!ticket) {
                    return;
                  }
                  if (
                    ticket.creditsRequired?.permission == "write" &&
                    !isPublished
                  ) {
                    return (
                      <>
                        <div className={styles.required}>
                          <div className={styles.requiredTitle}>
                            Credits Required:
                          </div>
                          <Checkbox
                            onChange={() => handleCheckBoxClick(ticket.id)}
                            checked={
                              ticket.creditsRequired.saved ??
                              ticket.creditsRequired.value
                            }
                          />
                        </div>
                        <div className={styles.creditHr} />
                      </>
                    );
                  } else if (ticket.creditsRequired?.permission !== "hidden") {
                    return (
                      <>
                        <div className={styles.required}>
                          <div className={styles.requiredTitle}>
                            Credits Required:
                          </div>
                          {ticket.creditsRequired.saved ??
                          ticket.creditsRequired.value
                            ? "Yes"
                            : "No"}
                        </div>
                      </>
                    );
                  }
                  return <></>;
                })()}
              </span>

              <div className={styles.typesTitle}>Accepted Credit Types:</div>
              <div className={styles.optionsListWrapper}>
                <div className={styles.optionsListHeader}>
                  <div className={styles.optionsHeader} />
                  <div className={styles.optionsHeader}>ID</div>
                  <div className={styles.optionsHeader}>Credit Type</div>
                </div>
                <div className={styles.optionsListBody}>
                  {acceptedCreditOptions.map((option, index) => (
                    <div
                      className={
                        index == acceptedCreditOptions.length - 1
                          ? styles.optionItemContainerLast
                          : styles.optionItemContainer
                      }
                      key={option.creditTypeId || index}
                    >
                      <div className={styles.optionItem}>
                        <Checkbox
                          className={styles.optionCheckbox}
                          onChange={() => {
                            const isOptionSelected = acceptedCreditArray.some(
                              (credit) =>
                                credit.creditTypeId === option.creditTypeId
                            );
                            if (isOptionSelected) {
                              setAcceptedCreditArray(
                                acceptedCreditArray.filter(
                                  (credit) =>
                                    credit.creditTypeId !== option.creditTypeId
                                )
                              );
                            } else {
                              setAcceptedCreditArray([
                                ...acceptedCreditArray,
                                option,
                              ]);
                            }
                          }}
                          checked={acceptedCreditArray.some(
                            (credit) =>
                              credit.creditTypeId === option.creditTypeId
                          )}
                          disabled={isPublished}
                        />
                      </div>
                      <div className={styles.optionItem}>
                        {option.creditTypeId}
                      </div>
                      <div className={styles.optionItem}>{option.name}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            {!isPublished ? (
              <Button
                className={styles.updateButton}
                children={"Update"}
                onClick={() => {
                  handleCreditChange();
                  setIsAcceptedCreditModalOpen(false);
                }}
              />
            ) : (
              <div className={styles.updateButtonFiller} />
            )}
          </div>
        }
      />

      <Modal
        title="Link Virtual Ticket"
        className={styles.virtualModal}
        onClose={() => {
          setIsVirtualModalOpen(false);
        }}
        isOpen={isVirtualModalOpen}
        children={
          <>
            <div className={styles.grid3}>
              <div className={styles.virtualEventList}>
                {virtualEvents
                  .find(
                    (e) =>
                      e.id ==
                      (marketingInfo.virtual.linkedEvent.saved.id ||
                        marketingInfo.virtual.linkedEvent.value.id)
                  )
                  ?.tickets?.map((tktSet) =>
                    tktSet?.tickets
                      ?.filter((ticket) => ticket.eventTicketId)
                      ?.map((ticket, index) => (
                        <div
                          className={
                            index !==
                            virtualEvents
                              .find(
                                (e) =>
                                  e.id ==
                                  (marketingInfo.virtual.linkedEvent.saved.id ||
                                    marketingInfo.virtual.linkedEvent.value.id)
                              )
                              ?.tickets?.tickets?.filter((t) => t.eventTicketId)
                              ?.length -
                              1
                              ? styles.virtualEvent
                              : styles.lastVirtualEvent
                          }
                          key={index}
                        >
                          {/* {console.log(ticket, selectedVirtualTicketId)} */}
                          <div className={styles.checkboxContainer}>
                            <Checkbox
                              className={styles.checkbox}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedVirtualTicketId(
                                    ticket.eventTicketId
                                  );
                                  setSelectedVirtualTicketName(
                                    ticket.name.saved || ticket.name.value
                                  );
                                } else {
                                  setSelectedVirtualTicketId(null);
                                  setSelectedVirtualTicketName(null);
                                }
                              }}
                              checked={
                                ticket.eventTicketId ===
                                  selectedVirtualTicketId &&
                                selectedVirtualTicketName ==
                                  (ticket.name.saved || ticket.name.value)
                              }
                            />
                          </div>
                          <div className={styles.name}>
                            {ticket.name.saved || ticket.name.value}
                          </div>
                        </div>
                      ))
                  )}
              </div>
              <Button
                className={styles.confirmVirtualEventBtn}
                children={"Confirm"}
                onClick={() => {
                  setIsVirtualModalOpen(false);
                  handleVirtualTicketChange();
                }}
              />
            </div>
          </>
        }
      />

      <Modal
        title="Dates"
        isOpen={isPurchaseDatesModalOpen}
        onClose={() => setIsPurchaseDatesModalOpen(false)}
        children={
          <div className={styles.datesModal}>
            <div className={styles.dateMessage}>
              Ticket dates will be set to the dates in{" "}
              {isLocalV2 ? "'Dates'" : "'Publishing Information'"} section
              unless changed here
            </div>
            <div className={styles.dateSelectors}>
              <div className={styles.purchaseDateSelectors}>
                <div className={styles.datesLabel}>
                  <label>Purchase</label>
                </div>
                <div className={styles.purchaseDates}>
                  <div className={styles.labelAndPickerContainer}>
                    <div className={styles.subLabelContainer}>
                      <label>Starts On:</label>
                    </div>

                    {(() => {
                      const selectedTicket = ticketSet.tickets.find(
                        (tkt) => tkt.id === selectedTicketId
                      );
                      const permission =
                        selectedTicket?.purchaseStartDate?.permission;
                      const {
                        timeZoneLabel,
                        dateString,
                        timeString,
                        displayDateTime,
                      } = getTicketFieldMeta(
                        selectedTicket,
                        "purchaseStartDate",
                        isLocalV2,
                        marketingInfo
                      );

                      const canEdit = canEditField(permission, true);
                      const canView = permission !== "hidden";

                      if (canEdit) {
                        return (
                          <div className={styles.datePickerContainer}>
                            <DatePicker
                              className={styles.date}
                              dateString={dateString}
                              setDate={(newDate) =>
                                updateUtcDateField(
                                  newDate,
                                  "purchaseStartDate",
                                  false
                                )
                              }
                            />
                            <div className={styles.timePickerContainer}>
                              <TimePicker
                                className={styles.date}
                                onChange={(newTime) =>
                                  updateUtcDateField(
                                    newTime,
                                    "purchaseStartDate",
                                    true
                                  )
                                }
                                timeString={timeString}
                              />
                            </div>
                          </div>
                        );
                      }

                      if (canView) {
                        return (
                          <div className={styles.readOnlyDateTimeZone}>
                            {displayDateTime}
                          </div>
                        );
                      }

                      return null;
                    })()}

                    {/* <div className={styles.timezone}>
                      {(() => {
                        const selectedTicket = ticketSet.tickets.find(
                          (tkt) => tkt.id === selectedTicketId
                        );
                        const permission =
                          selectedTicket?.purchaseStartTimeZone?.permission;
                        const rawTimeZone = getExplicitTicketTimeZoneLabel(
                          selectedTicket,
                          "purchaseStartDate",
                          isLocalV2
                        );

                        if (permission === "write" && !isPublished) {
                          return (
                            <Select
                              options={timeZones}
                              value={rawTimeZone || null}
                              placeholder={rawTimeZone || "Select Time Zone"}
                              onChange={(newTimeZone) =>
                                handleTimeZoneChange(newTimeZone)
                              }
                              disable={false}
                              className={styles.timezone}
                            />
                          );
                        }

                        if (permission !== "hidden") {
                          return (
                            <div
                              className={styles.readOnlyDateTimeZoneTimeZone}
                            >
                              {rawTimeZone || ""}
                            </div>
                          );
                        }

                        return null;
                      })()}
                    </div> */}
                    <div className={styles.timezoneSpacer}></div>
                  </div>

                  <div className={styles.labelAndPickerContainer}>
                    <div className={styles.subLabelContainer}>
                      <label>Ends On:</label>
                    </div>

                    {(() => {
                      const selectedTicket = ticketSet.tickets.find(
                        (tkt) => tkt.id === selectedTicketId
                      );
                      const permission =
                        selectedTicket?.purchaseEndDate?.permission;
                      const {
                        timeZoneLabel,
                        dateString,
                        timeString,
                        displayDateTime,
                      } = getTicketFieldMeta(
                        selectedTicket,
                        "purchaseEndDate",
                        isLocalV2,
                        marketingInfo
                      );

                      const canEdit = canEditField(permission, true);
                      const canView = permission !== "hidden";

                      if (canEdit) {
                        return (
                          <div className={styles.datePickerContainer}>
                            <DatePicker
                              className={styles.date}
                              dateString={dateString}
                              setDate={(newDate) =>
                                updateUtcDateField(
                                  newDate,
                                  "purchaseEndDate",
                                  false
                                )
                              }
                            />
                            <div className={styles.timePickerContainer}>
                              <TimePicker
                                className={styles.date}
                                onChange={(newTime) =>
                                  updateUtcDateField(
                                    newTime,
                                    "purchaseEndDate",
                                    true
                                  )
                                }
                                timeString={timeString}
                              />
                            </div>
                          </div>
                        );
                      }

                      if (canView) {
                        return (
                          <div className={styles.readOnlyDate}>
                            {displayDateTime}
                          </div>
                        );
                      }

                      return null;
                    })()}

                    <div className={styles.timezoneSpacer}></div>
                  </div>
                </div>
              </div>

              {!isLocalV2 && (
                <>
                  <div className={styles.otherDateSelectors}>
                    <div className={styles.otherDatesLabel}>
                      <label>Cancel Fee</label>
                    </div>
                    <div className={styles.labelAndPickerContainer2}>
                      {(() => {
                        const selectedTicket = ticketSet.tickets.find(
                          (tkt) => tkt.id === selectedTicketId
                        );
                        const permission =
                          selectedTicket?.startFeeDate?.permission;
                        const {
                          timeZoneLabel,
                          dateString,
                          timeString,
                          displayDateTime,
                        } = getTicketFieldMeta(
                          selectedTicket,
                          "startFeeDate",
                          isLocalV2,
                          marketingInfo
                        );

                        const canEdit = canEditField(permission, true);
                        const canView = permission !== "hidden";

                        if (canEdit) {
                          return (
                            <div className={styles.datePickerContainer}>
                              <DatePicker
                                className={styles.date}
                                dateString={dateString}
                                setDate={(newDate) =>
                                  updateUtcDateField(
                                    newDate,
                                    "startFeeDate",
                                    false
                                  )
                                }
                              />
                              <div className={styles.timePickerContainer}>
                                <TimePicker
                                  className={styles.date}
                                  onChange={(newTime) =>
                                    updateUtcDateField(
                                      newTime,
                                      "startFeeDate",
                                      true
                                    )
                                  }
                                  timeString={timeString}
                                />
                              </div>
                            </div>
                          );
                        }

                        if (canView) {
                          return (
                            <div className={styles.readOnlyDateSingle}>
                              {displayDateTime}
                            </div>
                          );
                        }

                        return null;
                      })()}

                      <div className={styles.timezoneSpacer}></div>
                    </div>
                  </div>

                  <div className={styles.otherDateSelectors}>
                    <div className={styles.otherDatesLabel}>
                      <label>Changeable Until</label>
                    </div>
                    <div className={styles.labelAndPickerContainer2}>
                      {(() => {
                        const selectedTicket = ticketSet.tickets.find(
                          (tkt) => tkt.id === selectedTicketId
                        );
                        const permission =
                          selectedTicket?.changeableUntilDate?.permission;
                        const {
                          timeZoneLabel,
                          dateString,
                          timeString,
                          displayDateTime,
                        } = getTicketFieldMeta(
                          selectedTicket,
                          "changeableUntilDate",
                          isLocalV2,
                          marketingInfo
                        );

                        const canEdit = canEditField(permission, true);
                        const canView = permission !== "hidden";

                        if (canEdit) {
                          return (
                            <div className={styles.datePickerContainer}>
                              <DatePicker
                                className={styles.date}
                                dateString={dateString}
                                setDate={(newDate) =>
                                  updateUtcDateField(
                                    newDate,
                                    "changeableUntilDate",
                                    false
                                  )
                                }
                              />
                              <div className={styles.timePickerContainer}>
                                <TimePicker
                                  className={styles.date}
                                  onChange={(newTime) =>
                                    updateUtcDateField(
                                      newTime,
                                      "changeableUntilDate",
                                      true
                                    )
                                  }
                                  timeString={timeString}
                                />
                              </div>
                            </div>
                          );
                        }

                        if (canView) {
                          return (
                            <div className={styles.readOnlyDateSingle}>
                              {displayDateTime}
                            </div>
                          );
                        }

                        return null;
                      })()}

                      <div className={styles.timezoneSpacer}></div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        }
      />
    </>
  );
}

TicketGroup.defaultProps = {
  ticketSet: { tickets: [] },
};

TicketGroup.propTypes = {
  pageMode: PropTypes.string,
  name: PropTypes.string,
  available: PropTypes.number,
  limit: PropTypes.string,
  ticketsPer: PropTypes.string,
  tickets: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      price: PropTypes.number,
      timeZone: PropTypes.string,
      startDate: PropTypes.string,
      endDate: PropTypes.string,
      startTime: PropTypes.string,
      endTime: PropTypes.string,
    })
  ),
  setAvailable: PropTypes.func,
  setLimit: PropTypes.func,
  setTicketsPer: PropTypes.func,
  updateTickets: PropTypes.func,
  eventStartDate: PropTypes.string,
  isMajorV1: PropTypes.bool,
};
